import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { BusinessType } from "@/lib/terminology";

export interface UserCommunity {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  cover_image_url: string | null;
  favicon_url: string | null;
  // Business configuration fields
  business_type: BusinessType;
  staff_title: string;
  staff_title_plural: string;
  booking_unit_name: string;
  // New fields from Batch 2
  timezone: string | null;
  city: string | null;
  country: string | null;
  website_url: string | null;
  brand_color: string | null;
  sms_enabled: boolean;
  currency: string | null;
}

/**
 * Fetches the community associated with the current user's profile.
 * Returns community data for customers, barbers, and shop admins.
 * Returns null if the user has no community_id or is a super_admin.
 */
export function useUserCommunity() {
  const { profile, roles, isSuperAdmin, loading: authLoading } = useAuth();
  const roleCommunityId = roles.find((role) => role.community_id)?.community_id ?? null;
  const communityId = profile?.community_id ?? roleCommunityId;

  return useQuery({
    queryKey: ["user-community", communityId],
    queryFn: async () => {
      if (!communityId) return null;

      const { data, error } = await (supabase as any)
        .from("communities")
        .select("id, name, slug, logo_url, cover_image_url, favicon_url, business_type, staff_title, staff_title_plural, booking_unit_name, timezone, city, country, website_url, brand_color, sms_enabled, currency")
        .eq("id", communityId)
        .maybeSingle();

      if (error) throw error;
      return data as UserCommunity | null;
    },
    // Enable for all users with a community except super admins. Prefer the
    // profile community, but fall back to role community so dashboard links do
    // not get stuck if profile sync lags behind role assignment.
    enabled: !authLoading && !!communityId && !isSuperAdmin,
  });
}
